/* eslint-disable local-rules/no-race-conditions */
/**
 * Root layout for DramaDetector Expo app
 *
 * Sets up:
 * - Console control (production log suppression)
 * - Analytics (Sentry + PostHog)
 * - Internationalization (i18next)
 * - React Query with offline persistence
 * - API client configuration (URLs, callbacks)
 * - Authentication context (AuthProvider)
 * - Protected routes (redirects based on auth state)
 * - Global styles (NativeWind)
 * - Offline support listeners
 * - Performance monitoring (Phase 6: Web Vitals + Font Load Tracking)
 */
import "../global.css";

// Must run FIRST - disables console.log/debug/info in production (web only)
import "../lib/console-control";

import React, { useEffect, useRef, useState, type ReactNode } from "react";
import { Stack, useRouter, useSegments, usePathname } from "expo-router";
import Head from "expo-router/head";
import { StatusBar } from "expo-status-bar";
import * as Linking from "expo-linking";
// Google Fonts (DD-261: HTML prototype visual parity)
import {
	useFonts,
	Inter_400Regular,
	Inter_500Medium,
	Inter_600SemiBold,
	Inter_700Bold,
} from "@expo-google-fonts/inter";
import { PlusJakartaSans_700Bold } from "@expo-google-fonts/plus-jakarta-sans";

// Initialize analytics BEFORE app renders (required)
// CRITICAL: PostHog MUST initialize BEFORE Sentry for session replay linking to work
// When Sentry's beforeSend hook runs, it needs PostHog session data to be available
import {
	initSentry,
	initPostHog,
	captureEvent,
	getTraceHeaders,
	getTelemetryHeaders,
	addBreadcrumb,
} from "../lib/analytics";
import { logger } from "../lib/logger";

type TelemetryBreadcrumb = {
	category: string;
	message: string;
	data?: Record<string, unknown>;
	level?: string;
};

type MinimalDocument = {
	createElement: (tag: string) => {
		defer?: boolean;
		src?: string;
		dataset: Record<string, string>;
	};
	head: {
		appendChild: (node: unknown) => void;
	};
};

// eslint-disable-next-line @typescript-eslint/no-namespace
declare namespace JSX {
	interface IntrinsicElements {
		meta: Record<string, unknown>;
	}
}

// Native performance baseline: mark module init start for startup span
// Sentry reactNativeTracingIntegration records app start automatically.
// We add a manual span here to capture JS module init time (PostHog + API client).
// @see https://docs.sentry.io/platforms/react-native/tracing/instrumentation/custom-instrumentation/
const _appStartTs = Date.now();

initPostHog(); // ✅ First - must be ready for Sentry integration
initSentry(); // ✅ Second - beforeSend hook will have access to PostHog session

// Configure logger telemetry (after initSentry)
logger.configureTelemetry((b: TelemetryBreadcrumb) => {
	// eslint-disable-next-line @typescript-eslint/no-require-imports
	require("@sentry/react-native").addBreadcrumb({
		category: b.category,
		message: b.message,
		data: b.data,
		level: b.level,
	});
});
// Note: analytics-wrapper auto-initializes on first event capture (lazy initialization)

import {
	initClientConfig,
	setupOfflineSupport,
} from "@dramadetector/api-client";
// FUTURE: Uncomment when app versioning is implemented
// import Constants from 'expo-constants';
import { useAuth } from "../contexts/AuthContext";
import { SafetyModalsManager } from "../components/safety";
// Phase 3c (NocoDB #3920): device.ts and device/storage imports removed from
// initClientConfig. Remaining getDeviceId calls in useEntitlements/useQuota/
// useCreateSharedLink/ConcernFlagModal are cleaned up in the follow-on PR.
import { ActivityIndicator, Platform, View } from "react-native";
import { i18n, getStoredLocale } from "@dramadetector/shared";
import { useOnboardingStatus } from "../hooks/useOnboardingStatus";
import { useOnboardingFlowFlag } from "../hooks/useFeatureFlags";
import { getAuthRouteAccessDecision } from "../lib/auth-route-access";
import { OnboardingFlow } from "../components/onboarding/OnboardingFlow";
import { usePerformanceMonitoring } from "../hooks/usePerformanceMonitoring";
import { useFontLoadTracking } from "../hooks/useFontLoadTracking";
import { SessionExpiryHandler } from "../components/SessionExpiryHandler";
import { initializePlugins } from "../lib/plugin-loader";
import { AppProviders } from "../components/AppProviders";
import { captureAndStoreUtmParams } from "../lib/utm";
import { addNotificationResponseListener } from "../lib/notifications";
import { useLastActive } from "../hooks/useLastActive";
import { useReEngagement } from "../hooks/useReEngagement";
import { WhatsNewModal } from "../components/WhatsNewModal";

// Precompiled regex for Better Auth callback detection (used in deep link handler).
// Hoisted to module scope to avoid re-compiling on every deep link event.
const BETTER_AUTH_CALLBACK_RE = /\/auth\/callback\/[a-z]+/i;

// Cloudflare Web Analytics — inject only when token is present (production only)
// EXPO_PUBLIC_CF_ANALYTICS_TOKEN is set in the production workflow .env.local only;
// staging and preview workflows omit it so no beacon fires there.
const webDocument = (
	globalThis as typeof globalThis & { document?: MinimalDocument }
).document;

if (
	Platform.OS === "web" &&
	webDocument &&
	process.env.EXPO_PUBLIC_CF_ANALYTICS_TOKEN
) {
	const _cfScript = webDocument.createElement("script");
	_cfScript.defer = true;
	_cfScript.src = "https://static.cloudflareinsights.com/beacon.min.js";
	_cfScript.dataset.cfBeacon = JSON.stringify({
		token: process.env.EXPO_PUBLIC_CF_ANALYTICS_TOKEN,
	});
	webDocument.head.appendChild(_cfScript);
}

// Initialize API client configuration (URLs + callbacks).
// Auth is handled entirely by Better Auth — no Supabase JS client needed.
// All edge function calls route through CF Workers.
initClientConfig({
	url: process.env.EXPO_PUBLIC_CF_WORKER_URL || "", // Base URL for edge function URL construction
	cfWorkerBaseUrl: process.env.EXPO_PUBLIC_CF_WORKER_URL || undefined,
	// Phase 3c (NocoDB #3920): getDeviceId/getDeviceToken/setDeviceToken removed.
	// Anonymous users now have BA anonymous JWTs (user_id-keyed quota).
	getPlatform: () => Platform.OS as "ios" | "android" | "web",
	// Distributed tracing (Sentry-only Phase 1)
	// @see packages/drama-detector-docs/features/analytics/OBSERVABILITY_HUB.md
	getTraceHeaders,
	// PostHog session linking (Phase 3 - January 2026)
	// Enables server-side events to appear in PostHog session replay timeline
	// @see OBSERVABILITY_HUB.md - "PostHog Session Linking" section
	getTelemetryHeaders: () => getTelemetryHeaders() as Record<string, string>,
	// Platform-agnostic Sentry exception capture for shared api-client hooks.
	// Injected here so @dramadetector/api-client avoids a direct @sentry/react-native import.
	captureException: (error: unknown, context?: Record<string, unknown>) =>
		// eslint-disable-next-line @typescript-eslint/no-require-imports
		require("@sentry/react-native").captureException(error, context),
});

/**
 * Performance Monitoring Component
 * Phase 6: Tracks Web Vitals + Font Load Times
 * Must render inside AuthProvider to access useAuth via useTier
 */
function PerformanceMonitoring() {
	usePerformanceMonitoring();
	useFontLoadTracking();
	return null; // No UI, just side effects
}

/**
 * ReEngagementManager - Tracks last activity and shows What's New modal (DD-160)
 *
 * Side-effect component that:
 * 1. Updates profiles.last_seen_at on every app foreground
 * 2. Shows WhatsNewModal for users inactive >7 days
 * 3. Gated by re_engagement_whats_new_enabled feature flag
 *
 * Must render inside AuthProvider and React Query provider.
 */
function ReEngagementManager() {
	useLastActive();
	const { showWhatsNew, daysInactive, dismissWhatsNew, onFeatureTap } =
		useReEngagement();

	return (
		<WhatsNewModal
			visible={showWhatsNew}
			daysInactive={daysInactive}
			onDismiss={dismissWhatsNew}
			onFeatureTap={onFeatureTap}
		/>
	);
}

/**
 * NavigationBreadcrumbs - Tracks route changes as Sentry breadcrumbs (#761)
 * Only fires on actual route transitions, not on every render.
 */
function NavigationBreadcrumbs() {
	const pathname = usePathname();
	const prevPathRef = useRef<string | null>(null);

	useEffect(() => {
		const prev = prevPathRef.current;
		if (prev !== null && prev !== pathname) {
			addBreadcrumb("navigation", "Route changed", {
				from: prev,
				to: pathname,
			});
		}
		prevPathRef.current = pathname;
	}, [pathname]);

	return null;
}

/**
 * OnboardingGate - Shows onboarding flow for new authenticated users (DD-159)
 *
 * Renders the multi-step onboarding flow when:
 * 1. User is authenticated (has session)
 * 2. profiles.is_onboarded is false
 * 3. onboarding_flow_enabled feature flag is true
 *
 * Otherwise renders children (normal app).
 */
function OnboardingGate({ children }: { children: ReactNode }) {
	const { accessToken, user } = useAuth();
	const { isOnboarded, isLoading, completeOnboarding } = useOnboardingStatus();
	const onboardingEnabled = useOnboardingFlowFlag();
	const pathname = usePathname();
	const [completing, setCompleting] = useState(false);

	// Never block these routes with onboarding
	const isExemptRoute =
		pathname.startsWith("/family-accept") ||
		pathname.startsWith("/s/") ||
		pathname.startsWith("/(auth)");

	// Show onboarding only for authenticated users who haven't completed it
	const shouldShowOnboarding =
		!!accessToken &&
		!!user &&
		!isOnboarded &&
		!isLoading &&
		onboardingEnabled &&
		!completing &&
		!isExemptRoute;

	const handleComplete = async () => {
		setCompleting(true);
		try {
			await completeOnboarding();
			// completing state prevents re-flash; the query will invalidate and
			// isOnboarded will become true on the next render cycle
		} catch (error) {
			console.error("[OnboardingGate] Failed to complete onboarding:", error);
			// Reset so the user can retry on next app launch
			setCompleting(false);
		}
	};

	if (shouldShowOnboarding) {
		return (
			<OnboardingFlow onComplete={handleComplete} displayName={user?.name} />
		);
	}

	return <>{children}</>;
}

/**
 * Protected Routes Component
 *
 * Routes traffic based on the 4 user states defined in
 * `docs/architecture/ANON_AUTHED_INTEGRATION.md`. Critical: gate on
 * `isAnonymous` (not `!accessToken`) — post-Phase-3c, anon BA users
 * have a JWT, so the old `!accessToken` predicate did not fire for
 * them and they bled into premium tabs (subscription, family-manage,
 * referrals). The current shape closes that gap.
 *
 * Anon users (pre- and post-JWT) can access:
 *   - home tab + (auth) screens
 *   - history / stats / settings tabs (first-class anon data screens)
 *   - any route containing `analyze` or `results` segments
 *   - /s/ shared links
 * Other tabs (subscription, family-manage, …) redirect anon to
 * `/signup?source=anon_premium_tab`.
 */
function ProtectedRoutes() {
	const { accessToken, loading, isAnonymous } = useAuth();
	const segments = useSegments();
	const router = useRouter();

	const routeAccess = getAuthRouteAccessDecision({
		segments: segments as unknown as string[],
		loading,
		isAnonymous,
		hasAccessToken: !!accessToken,
	});

	useEffect(() => {
		// Don't redirect while auth state is loading.
		// During loading we render a gate for protected routes so child screens
		// never mount and fire strict-auth hooks before eligibility is known.
		if (loading) return;

		// 1) Anon users (pre-JWT and post-JWT). Both have isAnonymous === true
		//    per AuthContext SSOT, so this branch handles them uniformly.
		if (routeAccess.redirectTo === "/signup?source=anon_premium_tab") {
			// Anon attempted a premium tab. Push to signup with source so the
			// PostHog funnel attributes the conversion entry point.
			addBreadcrumb("navigation", "Anon redirect: signup (premium tab)", {
				from: segments.join("/"),
				tab: routeAccess.tab ?? "unknown",
				reason: "anon_blocked_from_premium_tab",
			});
			router.replace("/signup?source=anon_premium_tab");
			return;
		}

		// 2) Logged out (no anon session). Allow home + auth group; redirect
		//    everything else to /login.
		if (routeAccess.redirectTo === "/login") {
			addBreadcrumb("navigation", "Auth redirect: login", {
				from: segments.join("/"),
				reason: "unauthenticated",
			});
			router.replace("/login");
			return;
		}

		// 3) Authed user on auth screens — bounce to home.
		if (routeAccess.redirectTo === "/") {
			addBreadcrumb("navigation", "Auth redirect: home", {
				from: segments.join("/"),
				reason: "already_authenticated",
			});
			router.replace("/");
		}
	}, [loading, routeAccess.redirectTo, routeAccess.tab, segments, router]);

	if (routeAccess.shouldRenderGate) {
		return (
			<View
				style={{
					flex: 1,
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: "#0a0012",
				}}
			>
				<ActivityIndicator size="large" color="#14B8A6" />
			</View>
		);
	}

	return (
		<Stack
			screenOptions={{
				// Prevent white flash during navigation by matching app's dark gradient background
				contentStyle: { backgroundColor: "#0a0012" },
				// Instant transition to eliminate white flash visibility window
				animation: "none",
			}}
		>
			<Stack.Screen name="(auth)" options={{ headerShown: false }} />
			<Stack.Screen name="(tabs)" options={{ headerShown: false }} />
			<Stack.Screen name="(practitioner)" options={{ headerShown: false }} />
			<Stack.Screen name="s" options={{ headerShown: false }} />
			<Stack.Screen name="family-accept" options={{ headerShown: false }} />
			<Stack.Screen
				name="practitioner-access"
				options={{ headerShown: false }}
			/>
			<Stack.Screen
				name="subscription-cancel"
				options={{ headerShown: false }}
			/>
			<Stack.Screen
				name="subscription-success"
				options={{ headerShown: false }}
			/>
			<Stack.Screen name="tier-demo" options={{ headerShown: false }} />
			{/* HowItWorks — persistent self-service help (Sprint 36 E1-S2-T2) */}
			{/* TODO(S37): Add nav link from header menu (DD-261 web navigation) */}
			<Stack.Screen name="how-it-works" options={{ headerShown: false }} />
			<Stack.Screen name="+not-found" options={{ headerShown: false }} />
		</Stack>
	);
}

export default function RootLayout() {
	// Load Google Fonts (DD-261: HTML prototype visual parity)
	const [fontsLoaded] = useFonts({
		Inter_400Regular,
		Inter_500Medium,
		Inter_600SemiBold,
		Inter_700Bold,
		PlusJakartaSans_700Bold,
	});

	// Set up offline support (network listener + app focus listener)
	useEffect(() => {
		const cleanup = setupOfflineSupport();
		return cleanup;
	}, []);

	// Handle notification taps (NocoDB #1281)
	// Wired here so deep-link routing has access to expo-router context.
	// Permission is NOT requested here — call register() from useNotificationPermission
	// at the right UX moment (e.g. after first successful analysis).
	useEffect(() => {
		if (Platform.OS === "web") return;
		const subscription = addNotificationResponseListener((response: any) => {
			const data = response.notification.request.content.data as Record<
				string,
				unknown
			>;
			const screen = typeof data?.screen === "string" ? data.screen : undefined;
			const notificationType =
				typeof data?.notification_type === "string"
					? data.notification_type
					: undefined;
			captureEvent("Notification_Tapped", {
				screen,
				notification_type: notificationType,
			});
			// TODO: add router.push(screen) once notification deep-link routes are defined
		});
		return () => subscription.remove();
	}, []);

	// Load persisted locale on app start (mobile only - web uses synchronous localStorage in config.ts)
	// Race Condition Fix (2025-01-15): Added await and isMounted cleanup flag
	useEffect(() => {
		let isMounted = true; // ✅ Cleanup flag

		async function loadPersistedLocale() {
			// Skip on web - already loaded synchronously in config.ts getInitialLocale()
			if (typeof window !== "undefined") {
				return;
			}

			try {
				// Mobile only: AsyncStorage is truly async, load persisted locale
				const storedLocale = await getStoredLocale();

				if (!isMounted) return; // ✅ Skip if unmounted

				if (storedLocale && ["en", "es"].includes(storedLocale)) {
					await i18n.changeLanguage(storedLocale); // ✅ Await async operation
				}
			} catch (error) {
				console.error("[_layout] Failed to load persisted locale:", error);
			}
		}

		loadPersistedLocale();

		return () => {
			isMounted = false; // ✅ Set cleanup flag
		};
	}, []);

	// Monitor URL changes for locale parameter (web only)
	useEffect(() => {
		if (typeof window === "undefined") return;

		const handleUrlChange = () => {
			const urlParams = new URLSearchParams(window.location.search);
			const urlLocale = urlParams.get("locale");
			if (
				urlLocale &&
				["en", "es"].includes(urlLocale) &&
				urlLocale !== i18n.language
			) {
				i18n.changeLanguage(urlLocale);
			}
		};

		// Listen to popstate (browser back/forward) and route changes
		window.addEventListener("popstate", handleUrlChange);

		// Also check on route changes (Expo Router navigation)
		handleUrlChange();

		return () => {
			window.removeEventListener("popstate", handleUrlChange);
		};
	}, []);

	// Handle deep links: Better Auth callbacks + UTM capture
	//
	// Better Auth OAuth callbacks (dramadetector://auth/callback/*) are handled
	// automatically by the @better-auth/expo plugin. We only listen here to
	// log routing events and capture UTM params from the launch URL.
	useEffect(() => {
		let isMounted = true;

		const handleDeepLink = (event: { url: string }) => {
			const url = event.url;

			// Better Auth callback — the expoClient plugin handles session exchange
			if (BETTER_AUTH_CALLBACK_RE.test(url)) {
				captureEvent("OauthCallback_Routed", {
					provider: "better-auth",
					url_pattern: "auth/callback/*",
				});
			}
		};

		// Subscribe to URL events
		const subscription = Linking.addEventListener("url", handleDeepLink);

		// Check if app was opened with a URL
		Linking.getInitialURL()
			.then((url: string | null) => {
				if (!isMounted) return;
				// Capture UTM params from launch URL (non-blocking, #795)
				captureAndStoreUtmParams(url).catch(() => {});
				if (url) {
					handleDeepLink({ url });
				}
			})
			.catch((error: unknown) => {
				console.error("[_layout] Failed to get initial URL:", error);
			});

		return () => {
			isMounted = false;
			subscription.remove();
		};
	}, []);

	// Initialize plugins at app startup (runtime context)
	// Week 5 Decision Gate instrumentation (Phase 1)
	// Fix #3: Option B (Graceful Degradation) - UNANIMOUS (3/3 AI models)
	useEffect(() => {
		initializePlugins().catch((error: unknown) => {
			// Error already tracked via Plugin_Error event in plugin-loader.ts
			console.error("[_layout] Plugin initialization failed:", error);
			// App continues startup (graceful degradation)
		});
	}, []);

	// Native performance baseline: record JS init span duration (task #1283)
	// Captures time from module evaluation (_appStartTs) to first render commit.
	// Sentry reactNativeTracingIntegration handles app start + navigation spans automatically.
	// This manual span covers the PostHog + API client init window specifically.
	useEffect(() => {
		if (Platform.OS === "web") return;

		const initDurationMs = Date.now() - _appStartTs;

		// Add breadcrumb for startup timing (visible in Sentry error detail)
		addBreadcrumb("startup", "JS module init complete", {
			init_duration_ms: initDurationMs,
			analytics: "posthog+sentry+api-client",
		});

		// Capture startup metric to PostHog for mobile health dashboard
		captureEvent("Performance_AppStartup", {
			init_duration_ms: initDurationMs,
			platform: Platform.OS,
		});
	}, []); // runs once after first render

	// ============================================================================
	// FIX (DRAMA-WEB-27): Always mount full provider tree to prevent hook errors
	// ============================================================================
	// ROOT CAUSE: Expo Router evaluates route modules before layout mounts.
	// Conditionally rendering children (with null) doesn't prevent route hooks
	// from running, causing "No QueryClient set" errors during font loading.
	//
	// SOLUTION: Always render the FULL component tree including all providers
	// and routes. Use CSS visibility/opacity to hide content until fonts load.
	// This ensures:
	// 1. React Query context is always available for hooks
	// 2. No FOUT (hidden until fonts loaded via opacity: 0)
	// 3. All route hooks can access required contexts
	//
	// ALTERNATIVE APPROACHES CONSIDERED:
	// - Suspense boundary: Doesn't prevent hook execution
	// - Conditional children: Expo Router still evaluates routes
	// - Defensive hook checks: Requires changes to all hook consumers
	// ============================================================================

	return (
		<AppProviders>
			{/* Default OG meta tags for social sharing (#2907) */}
			<Head>
				<meta property="og:type" content="website" />
				<meta property="og:site_name" content="DramaDetector" />
				<meta
					property="og:title"
					content="DramaDetector — AI-Powered Text Analysis"
				/>
				<meta
					property="og:description"
					content="Analyze messages for drama, conflict patterns, and communication style. Get actionable rewrites instantly."
				/>
				<meta
					property="og:image"
					content="https://dramadetector.com/og-image.png"
				/>
				<meta property="og:url" content="https://dramadetector.com" />
				<meta name="twitter:card" content="summary_large_image" />
			</Head>
			{/* FIX (DRAMA-WEB-27): Hide content until fonts load on web
          Uses CSS opacity instead of conditional rendering to prevent FOUT
          while keeping all hooks and providers mounted */}
			<FontLoadingGate fontsLoaded={fontsLoaded}>
				<PerformanceMonitoring />
				<NavigationBreadcrumbs />
				<SessionExpiryHandler />
				<ReEngagementManager />
				<OnboardingGate>
					<ProtectedRoutes />
				</OnboardingGate>
				{/* SafetyModalsManager coordinates ToS, Crisis, and Concern modals */}
				<SafetyModalsManager />
				<StatusBar style="auto" />
			</FontLoadingGate>
		</AppProviders>
	);
}

/**
 * FontLoadingGate - Hides content until fonts are loaded (web only)
 * Uses CSS opacity to prevent FOUT while keeping React tree mounted
 */
function FontLoadingGate({
	fontsLoaded,
	children,
}: {
	fontsLoaded: boolean;
	children: ReactNode;
}) {
	// On non-web platforms, always show content (fonts loaded synchronously or differently)
	if (Platform.OS !== "web") {
		return <>{children}</>;
	}

	// On web, hide content until fonts are loaded using opacity
	// Using opacity: 0 instead of display: none to maintain layout
	// and allow hooks to run normally
	return (
		<View
			style={{
				opacity: fontsLoaded ? 1 : 0,
				// Prevent layout shift by keeping full height
				flex: 1,
			}}
		>
			{children}
		</View>
	);
}
